import React, { useEffect, useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Play, Music, Plus, Trash2, Calendar, Clock } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import PlaylistCreatorDialog from '../components/PlaylistCreatorDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS_OF_WEEK = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LONG_LABELS = { monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday', thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday' };
const SLOT_KEYS = ['morning', 'afternoon', 'evening', 'night'];
const SLOT_LABELS = { morning: 'Morning', afternoon: 'Afternoon', evening: 'Evening', night: 'Night' };
const SLOT_TIMES = { morning: '06–12', afternoon: '12–17', evening: '17–21', night: '21–06' };
const SLOT_COLORS = {
  morning:   { ring: 'ring-[#FFD700]/40', text: 'text-[#FFD700]', bg: 'bg-[#FFD700]/8' },
  afternoon: { ring: 'ring-[#FF8C00]/40', text: 'text-[#FF8C00]', bg: 'bg-[#FF8C00]/8' },
  evening:   { ring: 'ring-[#FF007F]/40', text: 'text-[#FF007F]', bg: 'bg-[#FF007F]/8' },
  night:     { ring: 'ring-[#9D4CDD]/40', text: 'text-[#9D4CDD]', bg: 'bg-[#9D4CDD]/8' },
};

// Today + current-slot in IST (Asia/Kolkata). The backend's `current` uses
// UTC which can disagree with India venues by a 5h30 timezone shift — we
// re-derive it client-side so the "Today" / "Now" indicators are correct
// for the host's local clock.
function getISTNow() {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    weekday: 'long',
    hour: 'numeric',
    hour12: false,
  }).formatToParts(new Date());
  const weekday = (parts.find((p) => p.type === 'weekday')?.value || 'monday').toLowerCase();
  const hour = parseInt(parts.find((p) => p.type === 'hour')?.value || '0', 10);
  let day = weekday;
  let slot = 'night';
  if (hour >= 6 && hour < 12) slot = 'morning';
  else if (hour >= 12 && hour < 17) slot = 'afternoon';
  else if (hour >= 17 && hour < 21) slot = 'evening';
  else if (hour >= 0 && hour < 6) {
    // Late-night wrap — treat early hours as previous-day's night.
    const idx = DAYS_OF_WEEK.indexOf(weekday);
    day = DAYS_OF_WEEK[(idx - 1 + DAYS_OF_WEEK.length) % DAYS_OF_WEEK.length];
    slot = 'night';
  }
  return { day, slot };
}

const ScheduleGrid = ({ schedule, navigate, loading }) => {
  const [istNow, setIstNow] = React.useState(() => getISTNow());
  // Tick the IST clock every minute so the "Now" pill updates without a refresh.
  React.useEffect(() => {
    const t = setInterval(() => setIstNow(getISTNow()), 60_000);
    return () => clearInterval(t);
  }, []);
  const [selectedDay, setSelectedDay] = React.useState(istNow.day);
  // If the IST day changes (e.g. tab left open past midnight), keep the
  // selector in sync only if the user hasn't manually picked a different day.
  const userPickedRef = React.useRef(false);
  React.useEffect(() => {
    if (!userPickedRef.current) setSelectedDay(istNow.day);
  }, [istNow.day]);

  if (loading) {
    return (
      <div className="space-y-3">
        <div className="h-10 w-48 rounded-xl bg-white/[0.03] animate-pulse" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={`skel-${i}`} className="h-24 rounded-2xl bg-white/[0.03] animate-pulse" />
          ))}
        </div>
      </div>
    );
  }
  const data = schedule?.schedule || {};
  const daySlots = data[selectedDay] || {};
  const isToday = selectedDay === istNow.day;
  const curSlot = istNow.slot;

  return (
    <div className="space-y-4" data-testid="schedule-grid">
      <div className="flex items-center gap-3 flex-wrap" data-testid="schedule-day-selector">
        <Select
          value={selectedDay}
          onValueChange={(v) => { userPickedRef.current = true; setSelectedDay(v); }}
        >
          <SelectTrigger
            className="w-[200px] bg-[#0D0D0D] border-white/10 text-white font-['Outfit'] font-semibold h-11"
            data-testid="schedule-day-select-trigger"
          >
            <SelectValue placeholder="Pick a day" />
          </SelectTrigger>
          <SelectContent className="bg-[#0D0D0D] border-white/10 text-white">
            {DAYS_OF_WEEK.map((day) => {
              const assignedCount = SLOT_KEYS.reduce((n, s) => n + ((data[day] || {})[s] ? 1 : 0), 0);
              return (
                <SelectItem key={day} value={day} className="font-['Outfit'] focus:bg-white/5 focus:text-white">
                  <span className="flex items-center gap-2">
                    <span>{DAY_LONG_LABELS[day]}</span>
                    {day === istNow.day && (
                      <span className="text-[9px] uppercase tracking-[0.25em] text-[#39FF14] font-['JetBrains_Mono']">Today</span>
                    )}
                    <span className="text-[10px] text-gray-500">· {assignedCount}/4</span>
                  </span>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
        {isToday && (
          <span className="text-[10px] uppercase tracking-[0.3em] text-[#39FF14] font-['JetBrains_Mono'] inline-flex items-center gap-1">
            <Clock className="w-3 h-3" /> Today · IST
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
        {SLOT_KEYS.map((slot) => {
          const pl = daySlots[slot];
          const isNow = isToday && slot === curSlot;
          const palette = SLOT_COLORS[slot];
          return (
            <button
              key={`${selectedDay}-${slot}`}
              data-testid={`slot-${selectedDay}-${slot}`}
              onClick={() => pl && navigate(`/playlist/${pl.id}`)}
              disabled={!pl}
              className={`relative h-24 md:h-28 rounded-2xl text-left p-3 md:p-4 transition-all border ${
                pl
                  ? `bg-[#0D0D0D] border-white/10 hover:border-white/30 cursor-pointer ${isNow ? `ring-2 ring-offset-2 ring-offset-black ${palette.ring}` : ''}`
                  : 'bg-white/[0.02] border-dashed border-white/10 cursor-default'
              }`}
            >
              <div className="flex items-center justify-between mb-1.5">
                <span className={`text-[9px] md:text-[10px] uppercase tracking-[0.25em] font-['JetBrains_Mono'] font-bold ${palette.text}`}>
                  {SLOT_LABELS[slot]}
                </span>
                <span className="text-[9px] md:text-[10px] text-gray-600 font-['JetBrains_Mono']">{SLOT_TIMES[slot]}</span>
              </div>
              {pl ? (
                <div className="flex items-center gap-2.5 mt-1">
                  {pl.cover_image ? (
                    <img src={pl.cover_image} alt={pl.name} className="w-10 h-10 md:w-12 md:h-12 rounded-md object-cover flex-shrink-0" />
                  ) : (
                    <div className={`w-10 h-10 md:w-12 md:h-12 rounded-md flex items-center justify-center flex-shrink-0 ${palette.bg}`}>
                      <Music className={`w-4 h-4 md:w-5 md:h-5 ${palette.text}`} />
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="text-white text-xs md:text-sm font-semibold truncate font-['Outfit']">{pl.name}</p>
                    <p className="text-gray-500 text-[10px] md:text-[11px] truncate">{pl.track_count || 0} tracks</p>
                  </div>
                </div>
              ) : (
                <p className="text-gray-700 text-[11px] md:text-xs italic mt-2">— unassigned —</p>
              )}
              {isNow && (
                <span className="absolute top-1.5 right-1.5 inline-flex items-center gap-1 text-[9px] uppercase tracking-wider text-[#39FF14] font-['JetBrains_Mono'] bg-black/50 backdrop-blur px-1.5 py-0.5 rounded-full">
                  <Clock className="w-2.5 h-2.5" /> Now
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const PlaylistCardImpl = ({ playlist, onClick, onPlay, testId, showDelete, onDelete }) => (
  <div
    className="group bg-[#121212] border border-white/5 rounded-2xl p-4 transition-all hover:border-white/15 hover:bg-[#1A1A1A] cursor-pointer animate-fade-in"
    onClick={onClick}
    data-testid={testId}
  >
    <div className="relative aspect-square rounded-xl overflow-hidden mb-4">
      {playlist.cover_image ? (
        <img src={playlist.cover_image} alt={playlist.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-[#FF007F]/30 to-[#E11D48]/30 flex items-center justify-center">
          <Music className="w-12 h-12 text-white/30" />
        </div>
      )}
      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); if (onPlay) onPlay(playlist); }}
          className="w-14 h-14 rounded-full bg-[#FF007F] hover:bg-[#CC0066] flex items-center justify-center shadow-xl transform scale-0 group-hover:scale-100 transition-transform"
          data-testid={`play-playlist-${playlist.id}`}
          title="Load into queue and start playback"
          aria-label={`Play ${playlist.name}`}
        >
          <Play className="w-6 h-6 text-black ml-1" fill="currentColor" />
        </button>
      </div>
      {showDelete && (
        <button
          onClick={(e) => onDelete(e, playlist.id)}
          className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center hover:bg-red-600"
          data-testid={`delete-playlist-${playlist.id}`}
        >
          <Trash2 className="w-4 h-4 text-white" />
        </button>
      )}
    </div>
    <h3 className="font-semibold text-white truncate mb-1">{playlist.name}</h3>
    <p className="text-sm text-gray-400 line-clamp-2">{playlist.description || `${(playlist.tracks || []).length} songs`}</p>
  </div>
);

const PlaylistsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [curatedPlaylists, setCuratedPlaylists] = useState([]);
  const [myPlaylists, setMyPlaylists] = useState([]);
  const [schedule, setSchedule] = useState(null); // { schedule: {day: {slot: pl|null}}, current }
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  const isAI = user?.client_type === 'ai';

  useEffect(() => {
    // AI-typed venues stream from the in-house library (no venue-curated
    // playlists), so skip the fetch for them — the render early-exits below.
    if (isAI) return;
    const fetchData = async () => {
      // Promise.allSettled — if any one endpoint hiccups on a flaky mobile
      // network, the other two still hydrate the page. Previously Promise.all
      // rejected on the first failure, leaving the entire schedule grid blank
      // with no way to recover except a manual reload.
      const results = await Promise.allSettled([
        axios.get(`${API}/playlists/my-playlists`),
        axios.get(`${API}/playlists/created`),
        axios.get(`${API}/playlists/my-schedule`),
      ]);
      if (results[0].status === 'fulfilled') setCuratedPlaylists(results[0].value.data);
      if (results[1].status === 'fulfilled') setMyPlaylists(results[1].value.data);
      if (results[2].status === 'fulfilled') setSchedule(results[2].value.data);
      results.forEach((r, i) => {
        if (r.status === 'rejected') {
          console.warn(`[PlaylistsPage] fetch ${i} failed:`, r.reason?.message || r.reason);
        }
      });
      setLoading(false);
    };
    fetchData();
  }, [isAI]);

  // AI-typed venues never see this page — they stream from the in-house library.
  if (isAI) {
    return <Navigate to="/" replace />;
  }

  const openCreateDialog = () => setDialogOpen(true);

  const handlePlaylistSaved = (saved) => {
    setMyPlaylists((prev) => [saved, ...prev]);
  };

  const handleDeletePlaylist = async (e, playlistId) => {
    e.stopPropagation();
    if (!window.confirm('Delete this playlist?')) return;
    try {
      await axios.delete(`${API}/playlists/created/${playlistId}`);
      setMyPlaylists(prev => prev.filter(p => p.id !== playlistId));
      toast.success('Playlist deleted');
    } catch (error) {
      toast.error('Failed to delete playlist');
    }
  };

  // PlaylistCard component is defined at module-scope (PlaylistCardImpl) to avoid render-time recreation.
  const renderPlaylistCard = (playlist, opts) => (
    <PlaylistCardImpl
      key={playlist.id}
      playlist={playlist}
      onClick={opts.onClick}
      onPlay={opts.onPlay}
      testId={opts.testId}
      showDelete={opts.showDelete}
      onDelete={opts.onDelete}
    />
  );

  // Play button handler: load the playlist's tracks into the live queue
  // and navigate straight to the venue home so playback can start.
  const handlePlayPlaylist = async (playlist) => {
    const tracks = playlist?.tracks || [];
    if (tracks.length === 0) {
      toast.error("This playlist has no tracks yet");
      return;
    }
    try {
      await axios.post(
        `${API}/queue/load`,
        { tracks, start_index: 0 },
        { withCredentials: true },
      );
      toast.success(`Loaded "${playlist.name}" — heading to playback`);
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load playlist');
    }
  };

  return (
    <Layout>
      <div className="p-4 md:p-6 lg:p-8 pb-32 overflow-y-auto content-scroll" data-testid="playlists-page">
        {/* Welcome Section */}
        <div className="mb-6 md:mb-10">
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white font-['Outfit'] mb-2">
            Playlists
          </h1>
          <p className="text-gray-400 text-sm md:text-base">Your weekly schedule, curated mixes, and your own creations</p>
        </div>

        {/* Weekly schedule grid */}
        <section className="mb-8 md:mb-10" data-testid="playlists-schedule">
          <div className="flex items-center justify-between mb-4 md:mb-6">
            <div className="flex items-center gap-2 md:gap-3">
              <Calendar className="w-4 h-4 md:w-5 md:h-5 text-[#39FF14]" />
              <h2 className="text-lg md:text-2xl font-bold text-white font-['Outfit']">Weekly schedule</h2>
            </div>
            {schedule?.current?.playlist && (
              <span className="inline-flex items-center gap-1.5 md:gap-2 text-[9px] md:text-[10px] uppercase tracking-[0.2em] md:tracking-[0.3em] text-[#39FF14] font-['JetBrains_Mono']">
                <span className="w-1.5 h-1.5 rounded-full bg-[#39FF14] animate-pulse" />
                <span className="hidden sm:inline">Now: {schedule.current.day} · {schedule.current.slot}</span>
                <span className="sm:hidden">Now</span>
              </span>
            )}
          </div>
          <ScheduleGrid schedule={schedule} navigate={navigate} loading={loading} />
        </section>

        {/* My Playlists (Client-created) */}
        <section className="mb-10">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white font-['Outfit']">My Playlists</h2>
            <Button
              onClick={openCreateDialog}
              className="bg-[#FF007F] hover:bg-[#CC0066] text-black rounded-full"
              data-testid="create-playlist-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Playlist
            </Button>
          </div>

          {myPlaylists.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {myPlaylists.map((playlist) => renderPlaylistCard(playlist, {
                onClick: () => navigate(`/playlist/${playlist.id}?type=created`),
                onPlay: handlePlayPlaylist,
                testId: `my-playlist-${playlist.id}`,
                showDelete: true,
                onDelete: handleDeletePlaylist,
              }))}
            </div>
          ) : (
            <div className="bg-[#121212] border border-white/5 rounded-2xl p-8 text-center">
              <Music className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">No playlists yet</h3>
              <p className="text-gray-400 text-sm mb-4">Create your first playlist or import from YouTube</p>
              <Button onClick={openCreateDialog} className="bg-[#FF007F] hover:bg-[#CC0066] text-black rounded-full" data-testid="create-playlist-empty-button">
                <Plus className="w-4 h-4 mr-2" /> Create Playlist
              </Button>
            </div>
          )}
        </section>
      </div>

      {/* Create Playlist Dialog — tabbed Creator + Generator */}
      <PlaylistCreatorDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSaved={handlePlaylistSaved}
      />
    </Layout>
  );
};

export default PlaylistsPage;
